import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { getMonthName } from '../../../core/helper/month';
import { getTranslation } from '../../../core/helper/translation';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { standardCommand } from '../../../core/model/command';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { LocalDate } from '../../../core/model/local-date';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { EditCommandHandler } from '../../../core/service/command/edit-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { IncorrectPaymentCommand } from '../../command/incorrect-payment';
import { IncorrectPayment } from '../../model/incorrect-payment';
import { ChangePayerCommandHandler } from '../../service/command/change-payer-command-handler.servcice';
import { ContactBalanceCommandHandler } from '../../service/command/contact-balance-command-handler.service';
import { IncorrectPaymentCommandHandlerResolver } from '../../service/command/incorrect-payment-command-handler-resolver.service';
import { PayInvoiceCommandHandler } from '../../service/command/pay-invoice-command-handler.service';
import { VoidPaymentCommandHandler } from '../../service/command/void-payment-command-handler.service';
import { IncorrectPaymentService } from '../../service/data/incorrect-payment.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { LanguageService } from '../../../core/service/language.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';

@Component({
    selector: 'app-incorrect-payment-list',
    templateUrl: './incorrect-payment-list.component.html',
    styleUrls: ['./incorrect-payment-list.component.scss'],
    providers: [
        ChangePayerCommandHandler,
        IncorrectPaymentCommandHandlerResolver,
        PayInvoiceCommandHandler,
        ContactBalanceCommandHandler,
        VoidPaymentCommandHandler,
        EditCommandHandler,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class IncorrectPaymentListComponent
  extends PagedObjectManagerComponent<IncorrectPayment, IncorrectPaymentService>
  implements OnInit
{
  private canManageIncorrectPayments = false;

  constructor(
    dataService: IncorrectPaymentService,
    messageService: MessageService,
    private readonly _companyService: CompanyService,
    navigationService: NavigationService,
    commandDispatcher: CommandDispatcher,
    commandHandlerResolver: IncorrectPaymentCommandHandlerResolver,
    private readonly _languageService: LanguageService,
    accountService: UserAccountService,
    cdf: ColumnDefinitionFactory
  ) {
    super(dataService, messageService, navigationService, commandDispatcher, commandHandlerResolver);

    this.columnDefinitions = [
      cdf.createDate('PAYMENT.DATE', 'businessTransactionDate'),
      cdf.create('INCORRECT_PAYMENT.DESCRIPTION', 'description'),
      cdf.create('INCORRECT_PAYMENT.PAYER', 'payerNameAndAddress'),
      cdf.create('COMPANY.EDIT_HEADER', 'companyName', ColumnVisibility.optional),
      cdf.create('COMPANY.BANK_GIRO', 'bankgiroNumber'),
      cdf.createMoney('INCORRECT_PAYMENT.AMOUNT', 'amount'),
      cdf.createMoney('INCORRECT_PAYMENT.BALANCE', 'balance', {
        visibility: ColumnVisibility.default,
        helpText: 'INCORRECT_PAYMENT.HELP_TEXT.BALANCE',
      }),
      cdf.createMoney('INCORRECT_PAYMENT.BALANCE_ON_DATE', 'balanceOnDate', {
        visibility: ColumnVisibility.default,
        helpText: 'INCORRECT_PAYMENT.HELP_TEXT.BALANCE_ON_DATE',
      }),
      cdf.create('INVOICE.OCRNUMBER', 'ocr', ColumnVisibility.optional),
      cdf.create('PAYMENT.REGISTRATION_TYPE', 'registrationTypeText', ColumnVisibility.optional),
      cdf.createDate('INCORRECT_PAYMENT.CREATION_DATE', 'creationDate', ColumnVisibility.optional),
    ];
    this.contextMenuDefinition = [new MenuItemDefinition('EDIT_BTN', standardCommand.edit)];

    this.menuDefinition = [new MenuItemDefinition('LEASE_AGREEMENT.COMMAND.EXPORT', standardCommand.export)];
    this.AddSubscription(
      accountService.current.subscribe(account => {
        if (this.canManageIncorrectPayments || !account.hasAccess(ModuleNames.Invoicing, Features.ManageIncorrectPayments)) {
          return;
        }
        this.canManageIncorrectPayments = true;

        this.contextMenuDefinition.unshift(
          new MenuItemDefinition('INCORRECT_PAYMENT.COMMAND.PAY_INVOICE', IncorrectPaymentCommand.payInvoice),
          new MenuItemDefinition('INCORRECT_PAYMENT.COMMAND.CUSTOMER_BALANCE', IncorrectPaymentCommand.customerBalance),
          new MenuItemDefinition('INCORRECT_PAYMENT.COMMAND.CHANGE_PAYER', IncorrectPaymentCommand.changePayer),
          new MenuItemDefinition('INCORRECT_PAYMENT.COMMAND.VOID', IncorrectPaymentCommand.voidPayment)
        );
        this.menuDefinition.unshift(new MenuItemDefinition('INCORRECT_PAYMENT.COMMAND.VOID_MANY', IncorrectPaymentCommand.voidIncorrectPayments));
      })
    );
    this.preferencesKey = 'invoice.incorrect-payments';
  }

  ngOnInit() {
    let tran = this._languageService.getCurrentTranslations();

    this.AddSubscription(
      this.dataService.getFilterDefinition().subscribe(definition => {
        this.defaultFilters = definition.defaultFields;
        const showFromDates = definition.dateLimits.map(x => {
          return {
            caption: `${x.month ? getTranslation(tran, getMonthName(x.month)) : ''} ${x.year ? x.year + ' ' : ''}`,
            value: new LocalDate(x.year, x.month, 1).toISOString(),
          };
        });
        showFromDates.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: null });
        const yesNo = [
          { caption: 'NO_TEXT', value: false },
          { caption: 'YES_TEXT', value: true },
        ];
        const balanceOnDates = definition.dateLimits.map(x => {
          return {
            caption: `${x.month ? getTranslation(tran, getMonthName(x.month)) : ''} ${x.year ? x.year + ' ' : ''}`,
            value: new LocalDate(x.year, x.month, LocalDate.daysInMonth(x.year, x.month)).toISOString(),
          };
        });
        balanceOnDates.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: null });
        this.filterDefinition = [
          new FilterDefinitionItem(
            'COMPANY.LIST_HEADER',
            'company',
            definition.company,
            this._companyService,
            definition.selectedFields.includes('company')
          ),
          new FilterDefinitionItem(
            'INCORRECT_PAYMENT.FILTER.DATE_FROM',
            'fromDate',
            definition.fromDate,
            showFromDates,
            definition.selectedFields.includes('fromDate')
          ),
          new FilterDefinitionItem(
            'INCORRECT_PAYMENT.FILTER.MAX_BALANCE',
            'maxBalance',
            definition.maxBalance,
            null,
            definition.selectedFields.includes('maxBalance')
          ),
          new FilterDefinitionItem(
            'INCORRECT_PAYMENT.FILTER.INCLUDE_HANDLED',
            'includeHandled',
            definition.includeHandled,
            yesNo,
            definition.selectedFields.includes('includeHandled')
          ),
          new FilterDefinitionItem(
            'INCORRECT_PAYMENT.FILTER.PAYER_NAME',
            'payerName',
            definition.payerName,
            null,
            definition.selectedFields.includes('payerName')
          ),
          new FilterDefinitionItem(
            'INCORRECT_PAYMENT.FILTER.BALANCE_ON_DATE',
            'balanceOnDate',
            definition.balanceOnDate,
            balanceOnDates,
            definition.selectedFields.includes('balanceOnDate')
          ),
        ];
      })
    );
  }

  columnDefinitions: ColumnDefinition[];
  contextMenuDefinition: MenuItemDefinition[];
  itemsSource: IncorrectPayment[];
}
